import { useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { Loader2, AlertTriangle } from 'lucide-react';

export type ConfirmTone = 'default' | 'danger' | 'warning';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  /**
   * Si onConfirm devuelve una Promise, el botón muestra spinner y el modal
   * se cierra solo cuando la promise resuelve sin error.
   */
  onConfirm: () => void | Promise<void>;
}

const TONE_STYLES: Record<ConfirmTone, { button: string; icon: string; iconBg: string }> = {
  default: {
    button: 'bg-slate-900 hover:bg-slate-800 text-white',
    icon: 'text-slate-700',
    iconBg: 'bg-slate-100',
  },
  warning: {
    button: 'bg-amber-600 hover:bg-amber-700 text-white',
    icon: 'text-amber-700',
    iconBg: 'bg-amber-50',
  },
  danger: {
    button: 'bg-rose-600 hover:bg-rose-700 text-white',
    icon: 'text-rose-700',
    iconBg: 'bg-rose-50',
  },
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'default',
  onConfirm,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const styles = TONE_STYLES[tone];

  const handleConfirm = async () => {
    try {
      const result = onConfirm();
      if (result instanceof Promise) {
        setBusy(true);
        await result;
      }
      onOpenChange(false);
    } catch {
      // El handler debería haber mostrado un toast — no cerramos para que
      // el usuario pueda reintentar o cancelar.
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog.Root open={open} onOpenChange={busy ? undefined : onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <AlertDialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-sm bg-white rounded-2xl shadow-xl border border-slate-200 p-5 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95">
          <div className="flex items-start gap-3">
            {tone !== 'default' && (
              <div className={`shrink-0 p-2 rounded-full ${styles.iconBg}`}>
                <AlertTriangle size={18} className={styles.icon} />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <AlertDialog.Title className="text-base font-semibold text-slate-900">
                {title}
              </AlertDialog.Title>
              {description && (
                <AlertDialog.Description className="text-sm text-slate-600 mt-1.5 leading-relaxed">
                  {description}
                </AlertDialog.Description>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 mt-5">
            <AlertDialog.Cancel asChild>
              <button
                disabled={busy}
                className="px-3 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              >
                {cancelLabel}
              </button>
            </AlertDialog.Cancel>
            <button
              onClick={handleConfirm}
              disabled={busy}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg disabled:opacity-40 ${styles.button}`}
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              {confirmLabel}
            </button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
